import { AlertTriangle, Clock } from 'react-feather';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  ExportFormat,
  GetRegistrationsQuery as GetRegistrations,
  Permission,
  RegistrationStatus,
} from '__generated__/graphql';

import { ExportsContext } from '../../Exports/ExportsProvider';
import { TicketLabel } from '../../../common/Common/SellableLabel';
import { assetUrl, encodeUrl, isEmpty, isGraphQLValidationError } from '../../../common/helpers';
import ExportRegistrationsForm from '../ExportRegistrationsForm';
import GetRegistrationsQuery from './GetRegistrationsQuery';
import NoEventsCard from '../NoEventsCard';
import ProtectedSection from '../../Common/ProtectedSection';
import RegistrationFilters, {
  PurchaseStatsData, RegistrationFilterParams, mapRegistrationFiltersToServerInput, parseRegistrationFiltersUrlState,
} from './RegistrationFilters';
import UI from '../../../common/UI';
import config from '../../config';
import useAddOrderModal from '../Participant/Orders/useAddOrderModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useUrlState from '../../../common/useUrlState';

type Registration = GetRegistrations['project']['registrations']['data'][0];

const RegistrationsPage = () => {
  const { t } = useTranslation();
  const history = useHistory<{ registration_filters?: RegistrationFilterParams; }>();
  const device = useMediaDevice();
  const project = useProject();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('registrations'));

  const [filtersKey, setFiltersKey] = useState(0);

  const [{ page, ...urlFilters }, setUrlState] = useUrlState({
    parse: (params, { integer }) => ({
      ...parseRegistrationFiltersUrlState(params),
      page: integer(params.page, 1),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Registrations' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  const filters = {
    ...urlFilters,
    event: recentEvent.id,
  };

  const handleFiltersChange = useCallback((newFilters: RegistrationFilterParams) => {
    setUrlState(() => ({
      ...newFilters,
      page: null,
    }), true);
  }, [setUrlState]);

  // State that contains all filter stats
  const [filterStats, setFilterStats] = useState<PurchaseStatsData>();

  const registrationsCount = filterStats?.tickets || 0;
  const enableExport = registrationsCount > 0 && registrationsCount <= config.maxExportSize.registrations;

  const handleStatsChange = (stats: PurchaseStatsData) => {
    setFilterStats(stats);
  };

  /** Filters object that can be sent to the server */
  const registrationFilters = mapRegistrationFiltersToServerInput(filters);

  // Fetches the filtered participants
  const { data, loading } = useQuery(GetRegistrationsQuery, {
    variables: {
      ...registrationFilters,
      project_id: project.id,
      page,
      per_page: 20,
    },
    onError: (error) => {
      if (isGraphQLValidationError(error)) {
        // Recent event no longer exists, reset it.
        recentEvent.reset();
      }
    },
  });

  const events = data?.project.events;
  const registrations = data?.project.registrations;

  const [showEvent, setShowEvent] = useState(!filters.event);
  const registrationsRef = useRef(registrations);

  // Show event labels after new registrations have been retrieved.
  useEffect(() => {
    if (registrationsRef.current !== registrations) {
      setShowEvent(!filters.event);
    }
    registrationsRef.current = registrations;
  }, [registrations, filters.event]);

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((filter) => !isEmpty(filter));

  const [openAddRegistrationModal] = useAddOrderModal({
    eventId: filters.event,
    onSuccess: (order) => {
      history.push(`/${project.id}/participants/${order.participant.id}/orders/${order.id}`);
    },
  });

  const { openExportsModal } = useContext(ExportsContext);

  const [openExportRegistrationsModal, closeExportRegistrationsModal] = useModal(() => (
    <UI.Modal maxWidth={800} close={closeExportRegistrationsModal}>
      <ExportRegistrationsForm
        filters={registrationFilters}
        registrationsCount={registrationsCount}
        onSuccess={({ format }) => {
          closeExportRegistrationsModal();
          openExportsModal(format === ExportFormat.mailchimp ? 'mailchimp' : 'recent_exports');
        }}
      />
    </UI.Modal>
  ), [filters, registrationsCount, openExportsModal]);

  if (!events) {
    return <UI.PageLoader />;
  }

  if (events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.GridContainer sc={{ columns: ['2fr 3fr'], alignVertical: 'center' }}>
          <UI.Div>
            <UI.SectionHeading>
              {t('registrations')}
            </UI.SectionHeading>
          </UI.Div>
          <UI.FlexContainer sc={{ justifyContent: 'end', spaceX: 1 }}>
            <ProtectedSection permissions={[Permission.edit_registrations]}>
              <UI.Button
                onClick={openAddRegistrationModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_registration')}
              </UI.Button>
            </ProtectedSection>
            {!device.isSmall && (
              <ProtectedSection permissions={[Permission.exports]}>
                <UI.TooltipContainer>
                  {registrationsCount > config.maxExportSize.registrations && (
                    <UI.Tooltip sc={{ alignH: 'center', alignV: 'bottom' }}>
                      <UI.Icon>
                        <AlertTriangle />
                      </UI.Icon>
                      {' '}
                      {t('export_too_large')}
                    </UI.Tooltip>
                  )}
                  <UI.Button
                    onClick={() => openExportRegistrationsModal()}
                    disabled={!enableExport}
                  >
                    {t('export')}
                  </UI.Button>
                </UI.TooltipContainer>
              </ProtectedSection>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <RegistrationFilters
              filters={filters}
              onChange={handleFiltersChange}
              onStatsChange={handleStatsChange}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
              {registrations.data.map((registration) => (
                <RegistrationCard
                  registration={registration}
                  showEvent={showEvent}
                  key={registration.id}
                />
              ))}

              {registrations.data.length === 0 && (
                <>
                  {hasFilters && (
                    <UI.Card
                      sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                      style={{ textWrap: 'balance' }}
                    >
                      <UI.Div>
                        <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      </UI.Div>
                      {t('no_registrations_match_filters')}
                      {' '}
                      <UI.Link to={`/${project.id}/participants`}>{t('reset_filters')}</UI.Link>
                    </UI.Card>
                  )}

                  {!hasFilters && !loading && (
                    <UI.Card
                      sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                      style={{ textWrap: 'balance' }}
                    >
                      <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      <UI.H3 sc={{ mb: 2 }}>{t('no_registrations_yet')}</UI.H3>
                      <UI.Paragraph sc={{ mb: 0 }}>
                        {t('no_registrations_get_started')}
                      </UI.Paragraph>
                    </UI.Card>
                  )}
                </>
              )}
            </UI.GridContainer>

            {(page > 1 || registrations.has_more_pages) && (
              <UI.Div sc={{ mt: 4 }}>
                <UI.Pager disableNext={!registrations.has_more_pages} scroll />
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

const RegistrationCard = ({
  registration,
  showEvent = false,
}: {
  registration: Registration;
  showEvent?: boolean;
}) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatDate, parseDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();
  const device = useMediaDevice();

  const { participant } = registration;
  const to = `/${project.id}/participants/${participant.id}?${encodeUrl({ event: registration.event.id, search: `ATL${registration.sequence}` })}`;

  return (
    <UI.NavCard to={to}>
      <UI.GridContainer sc={{ columns: ['3fr 2fr 80px', '1fr 1fr 1fr 80px'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4
            sc={{
              muted: !registration.purchase.active || !registration.assigned,
              deleted: registration.status === RegistrationStatus.cancelled,
              fontWeight: !registration.assigned ? 400 : undefined,
            }}
          >
            <UI.Truncate>
              {registration.assigned
                ? registration.full_name
                : (registration.invited_email ? t('invited') : t('not_assigned_yet'))}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        {!device.isSmall && (
          <UI.Div sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              {(!registration.assigned && registration.invited_email) || registration.participant.email}
            </UI.Truncate>
          </UI.Div>
        )}
        <UI.FlexContainer sc={{ spaceX: 1 }} style={{ minWidth: 0 }}>
          {showEvent ? (
            <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
              <UI.Truncate>
                {registration.event.title}
              </UI.Truncate>
            </UI.Label>
          ) : (
            <>
              <TicketLabel
                ticket={registration.ticket.title}
                status={registration.status}
                sc={{ block: true }}
                style={{ minWidth: 0 }}
              />
              {!device.isSmall && registration.status === RegistrationStatus.waitlist && (
                <UI.Label sc={{ small: true }}>
                  <UI.Icon>
                    <UI.Icons.Hourglass />
                  </UI.Icon>
                  {' '}
                  {t('waitlist')}
                </UI.Label>
              )}
              {!device.isSmall && registration.time_slot && (
                <UI.Label sc={{ small: true }}>
                  <UI.Icon>
                    <Clock />
                  </UI.Icon>
                  {' '}
                  {formatTimeSlot({ ...registration.time_slot, multi_date: false })}
                </UI.Label>
              )}
              {!device.isSmall && registration.ticket_number && (
                <UI.Label sc={{ small: true }}>
                  <UI.Icon>
                    <UI.Icons.StartFinish />
                  </UI.Icon>
                  {' '}
                  {registration.ticket_number}
                </UI.Label>
              )}
            </>
          )}
        </UI.FlexContainer>
        <UI.Div sc={{ small: true, color: 'gray.500', textAlign: 'right' }}>
          {formatDate(
            parseDate(registration.created_at, { timezone: 'UTC' }),
            { format: 'display_compact_date' },
          )}
        </UI.Div>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

export default RegistrationsPage;
